import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { ArrowLeft, Save, Send, Trash2, Monitor, Smartphone, Loader2, CheckCircle2, AlertCircle, Blocks, Code as CodeIcon, Star } from "lucide-react";
import BlockEditor from "@/components/BlockEditor";
import { renderBlocksToHtml } from "@/lib/blockRenderer";
import { STARTER_TEMPLATES } from "@/lib/blockTemplates";

export default function CampaignEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [blocks, setBlocks] = useState(null);   // null = HTML mode, [] = block mode
  const [mode, setMode] = useState("html");      // "blocks" | "html"
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewport, setViewport] = useState("desktop");
  const [testEmail, setTestEmail] = useState("");
  const [testStatus, setTestStatus] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const [err, setErr] = useState("");

  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/campaigns/${id}`);
      setC(data);
      setName(data.name); setSubject(data.subject); setHtml(data.html);
      if (Array.isArray(data.blocks) && data.blocks.length > 0) {
        setBlocks(data.blocks);
        setMode("blocks");
      } else if (!data.html || !data.html.trim()) {
        // Empty campaign → open Visual Blocks mode so the template picker is shown
        setBlocks([]);
        setMode("blocks");
      } else {
        setBlocks(null);
        setMode("html");
      }
      setDirty(false);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail));
    }
  }, [id]);

  useEffect(() => { load(); api.get("/recipients/stats").then(r => setActiveCount(r.data.active)); }, [load]);

  // Live poll if sending
  useEffect(() => {
    if (!c) return;
    if (c.status === "sending") {
      pollRef.current = setInterval(load, 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [c?.status, load]);

  const onChange = (setter) => (e) => { setter(e.target.value); setDirty(true); };

  const onBlocksChange = (next) => {
    setBlocks(next);
    setHtml(renderBlocksToHtml(next));
    setDirty(true);
  };

  const switchToBlocks = (startFromTemplate) => {
    if (startFromTemplate) {
      const tpl = STARTER_TEMPLATES.find(t => t.id === startFromTemplate);
      if (tpl) {
        const cloned = JSON.parse(JSON.stringify(tpl.blocks));
        setBlocks(cloned);
        setHtml(renderBlocksToHtml(cloned));
        // Adopt template's subject line unless the user has already typed one
        if (tpl.subject && !subject.trim()) setSubject(tpl.subject);
        setMode("blocks");
        setDirty(true);
        setShowTemplatePicker(false);
        return;
      }
    }
    // No template: start blank with header+footer
    const blank = STARTER_TEMPLATES.find(t => t.id === "blank");
    const cloned = JSON.parse(JSON.stringify(blank.blocks));
    setBlocks(cloned);
    setHtml(renderBlocksToHtml(cloned));
    setMode("blocks");
    setDirty(true);
    setShowTemplatePicker(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name, subject, html };
      if (mode === "blocks" && Array.isArray(blocks)) {
        payload.blocks = blocks;
        payload.html = renderBlocksToHtml(blocks);
      } else {
        // explicit HTML mode: clear blocks
        payload.blocks = [];
      }
      const { data } = await api.patch(`/campaigns/${id}`, payload);
      setC(data); setDirty(false);
    } catch (e) {
      alert(formatErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTestStatus({ loading: true });
    try {
      // Auto-save any unsaved edits so the DB has the latest subject/html
      // before Mailgun is called. Otherwise test-send returns "subject and html required".
      if (dirty) {
        const payload = { name, subject, html };
        if (mode === "blocks" && Array.isArray(blocks)) {
          payload.blocks = blocks;
          payload.html = renderBlocksToHtml(blocks);
        } else {
          payload.blocks = [];
        }
        await api.patch(`/campaigns/${id}`, payload);
        setDirty(false);
      }
      // Guard: if subject/html are still empty after save (e.g. template picked but nothing typed),
      // surface a clear message instead of hitting the backend and getting a generic 400.
      if (!subject.trim() || !(html || "").trim()) {
        setTestStatus({ error: "Please add a subject line and content before sending a test." });
        return;
      }
      const { data } = await api.post("/campaigns/test-send", { campaign_id: id, to: testEmail });
      setTestStatus({
        ok: true,
        isList: !!data?.is_mailing_list,
        memberCount: data?.member_count,
        listAddress: data?.list_address,
      });
      setTimeout(() => setTestStatus(null), 8000);
    } catch (e) {
      setTestStatus({ error: formatErr(e.response?.data?.detail) });
    }
  };

  const sendAll = async (selectedGroups) => {
    try {
      await api.post(`/campaigns/${id}/send`, { groups: selectedGroups });
      setSendOpen(false);
      load();
    } catch (e) {
      alert(formatErr(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this campaign?")) return;
    try { await api.delete(`/campaigns/${id}`); nav("/campaigns"); }
    catch (e) { alert(formatErr(e.response?.data?.detail)); }
  };

  if (!c) {
    return <div className="p-12 text-sm" style={{ color: "var(--rahi-soft)" }}>{err || "Loading…"}</div>;
  }

  const isLocked = c.status === "sending" || c.status === "completed";
  const progress = c.total_recipients ? Math.round((c.sent_count / c.total_recipients) * 100) : 0;

  return (
    <div className="min-h-screen flex flex-col" data-testid="campaign-editor-page">
      {/* Top bar */}
      <div className="sticky top-0 z-10 border-b" style={{ borderColor:"var(--rahi-border)", background:"rgba(253,252,251,.95)", backdropFilter:"blur(8px)" }}>
        <div className="px-8 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-4 min-w-0">
            <button onClick={()=>nav("/campaigns")} className="btn btn-ghost" data-testid="back-btn">
              <ArrowLeft size={14}/>
            </button>
            <input
              value={name} onChange={onChange(setName)} disabled={isLocked}
              className="font-display text-xl font-black bg-transparent border-0 focus:outline-none focus:ring-0 tracking-tight w-[420px]"
              data-testid="campaign-name-input"
            />
            <span className={`badge ${c.status === "sending" ? "badge-sending" : c.status === "completed" ? "badge-completed" : c.status === "failed" ? "badge-failed" : "badge-draft"}`}>
              {c.status}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {!isLocked && (
              <>
                <button onClick={save} disabled={!dirty || saving} className="btn btn-ghost" data-testid="save-btn">
                  <Save size={14}/> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
                </button>
                <button onClick={remove} className="btn btn-ghost" style={{ color:"var(--rahi-red)" }} data-testid="delete-btn">
                  <Trash2 size={14}/>
                </button>
                <button onClick={()=>setSendOpen(true)} className="btn btn-primary" data-testid="send-btn" disabled={dirty}>
                  <Send size={14}/> Send to {activeCount.toLocaleString("en-IN")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Sending progress banner */}
      {c.status === "sending" && (
        <div className="px-8 py-6 border-b" style={{ borderColor:"var(--rahi-border)", background:"#FFF7F5" }} data-testid="sending-banner">
          <div className="flex items-center gap-4 mb-3">
            <Loader2 size={18} className="animate-spin" style={{ color:"var(--rahi-red)" }}/>
            <div className="flex-1">
              <div className="font-medium text-sm">Sending in progress…</div>
              <div className="text-xs mt-0.5" style={{ color:"var(--rahi-muted)" }}>
                {c.sent_count.toLocaleString("en-IN")} of {c.total_recipients.toLocaleString("en-IN")} queued with Mailgun ({progress}%)
              </div>
            </div>
            <div className="font-display text-3xl font-black tracking-tight">{progress}%</div>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background:"#FBE5DE" }}>
            <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, background:"var(--rahi-red)" }} />
          </div>
        </div>
      )}

      {c.status === "completed" && <CompletedSummary c={c}/>}
      {c.status === "failed" && (
        <div className="px-8 py-4 border-b text-sm flex gap-2 items-center" style={{ borderColor:"var(--rahi-border)", background:"#FEF2F2", color:"#B91C1C" }}>
          <AlertCircle size={16}/> Send failed: {c.error || "Unknown error"}
        </div>
      )}

      {/* Editor + Preview */}
      <div className="flex-1 grid lg:grid-cols-[480px_1fr]">
        {/* LEFT: form */}
        <div className="border-r overflow-y-auto" style={{ borderColor:"var(--rahi-border)", maxHeight:"calc(100vh - 73px)" }}>
          <div className="p-8 pb-4">
            <label className="label">Subject line</label>
            <input value={subject} onChange={onChange(setSubject)} disabled={isLocked} className="input" data-testid="subject-input"/>
            <p className="hint">Use <span className="font-mono">%recipient.name%</span> to personalize.</p>
          </div>

          {/* Mode tabs */}
          <div className="px-8 sticky top-0 z-[1] pt-2 pb-3" style={{ background:"var(--rahi-bg)", borderBottom:"1px solid var(--rahi-border)" }}>
            <div className="flex gap-1 p-1 rounded-md border w-fit" style={{ borderColor:"var(--rahi-border)", background:"#fff" }}>
              <button
                onClick={() => setMode("blocks")} disabled={isLocked}
                className={`px-3 py-1.5 rounded text-xs flex items-center gap-1.5 ${mode === "blocks" ? "bg-[#1C1917] text-white" : "text-[#57534E]"}`}
                data-testid="mode-blocks-btn"
              ><Blocks size={12}/> Visual blocks</button>
              <button
                onClick={() => setMode("html")} disabled={isLocked}
                className={`px-3 py-1.5 rounded text-xs flex items-center gap-1.5 ${mode === "html" ? "bg-[#1C1917] text-white" : "text-[#57534E]"}`}
                data-testid="mode-html-btn"
              ><CodeIcon size={12}/> HTML source</button>
            </div>
          </div>

          {mode === "blocks" ? (
            <div className="px-8 py-6">
              {!blocks || blocks.length === 0 ? (
                <div className="space-y-4">
                  <p className="text-sm" style={{ color: "var(--rahi-muted)" }}>
                    Start with a polished template, or build from a blank canvas.
                  </p>
                  <div className="grid grid-cols-1 gap-3">
                    {STARTER_TEMPLATES.map(t => (
                      <button
                        key={t.id}
                        onClick={() => switchToBlocks(t.id)}
                        disabled={isLocked}
                        className="text-left p-4 border rounded-md hover:border-[var(--rahi-red)] hover:bg-[#FFF7F5] transition"
                        style={{ borderColor: "var(--rahi-border)", background: "#fff" }}
                        data-testid={`template-${t.id}`}
                      >
                        <div className="font-medium text-sm mb-1">{t.name}</div>
                        <div className="text-xs" style={{ color: "var(--rahi-muted)" }}>{t.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <BlockEditor blocks={blocks} onChange={onBlocksChange} disabled={isLocked}/>
              )}
            </div>
          ) : (
            <div className="px-8 py-6">
              <label className="label">HTML body</label>
              <textarea
                value={html} onChange={onChange(setHtml)} disabled={isLocked}
                className="textarea" rows={20}
                data-testid="html-input"
              />
              <p className="hint">
                Placeholders available: <span className="font-mono">%recipient.name%</span>,{" "}
                <span className="font-mono">%recipient.unsubscribe_url%</span>
              </p>
            </div>
          )}

          {!isLocked && (
            <div className="card p-5 m-8 mt-0 space-y-3" style={{ background:"#FBFAF9" }}>
              <div className="text-[11px] uppercase tracking-[0.12em] font-medium" style={{ color:"var(--rahi-muted)" }}>Test send</div>
              <input
                type="email" placeholder="your.email@example.com"
                value={testEmail} onChange={(e)=>setTestEmail(e.target.value)}
                className="input" data-testid="test-email-input"
              />
              <button onClick={sendTest} disabled={!testEmail || testStatus?.loading} className="btn btn-ghost w-full justify-center" data-testid="test-send-btn">
                {testStatus?.loading ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
                Send test
              </button>
              {testStatus?.ok && (
                <div className="text-xs flex gap-2 items-start" style={{ color:"#047857" }}>
                  <CheckCircle2 size={12} style={{ marginTop: 2 }}/>
                  <div>
                    {testStatus.isList
                      ? <>Sent to <strong>{testStatus.memberCount}</strong> members of <span className="font-mono">{testStatus.listAddress}</span>.</>
                      : "Sent."}
                  </div>
                </div>
              )}
              {testStatus?.error && <div className="text-xs" style={{ color:"#B91C1C" }}>{testStatus.error}</div>}
            </div>
          )}
        </div>

        {/* RIGHT: live preview */}
        <div className="p-8 overflow-y-auto" style={{ background:"#f5f3f0", maxHeight:"calc(100vh - 73px)" }}>
          <div className="flex justify-between items-center mb-4">
            <p className="text-[11px] uppercase tracking-[0.2em]" style={{ color:"var(--rahi-soft)" }}>Live preview</p>
            <div className="flex gap-1 p-1 rounded-md border" style={{ borderColor:"var(--rahi-border)", background:"#fff" }}>
              <button
                onClick={()=>setViewport("desktop")}
                className={`px-3 py-1 rounded text-xs flex items-center gap-1.5 ${viewport==="desktop" ? "bg-[#1C1917] text-white" : "text-[#57534E]"}`}
                data-testid="preview-desktop-btn"
              ><Monitor size={12}/> Desktop</button>
              <button
                onClick={()=>setViewport("mobile")}
                className={`px-3 py-1 rounded text-xs flex items-center gap-1.5 ${viewport==="mobile" ? "bg-[#1C1917] text-white" : "text-[#57534E]"}`}
                data-testid="preview-mobile-btn"
              ><Smartphone size={12}/> Mobile</button>
            </div>
          </div>
          <div className="mx-auto bg-white shadow-xl rounded-md overflow-hidden transition-all" style={{ width: viewport==="mobile" ? 380 : 720, maxWidth:"100%" }}>
            <iframe
              title="preview"
              srcDoc={renderPreview(html, subject)}
              className="w-full"
              style={{ minHeight:"80vh", border:0 }}
              data-testid="preview-iframe"
            />
          </div>
        </div>
      </div>

      {sendOpen && (
        <SendConfirmModal
          campaign={c}
          activeCount={activeCount}
          onCancel={()=>setSendOpen(false)}
          onConfirm={sendAll}
        />
      )}
    </div>
  );
}

function renderPreview(html, subject) {
  // Replace Mailgun placeholders with sample values for preview
  return (html || "")
    .replaceAll("%recipient.name%", "Friend")
    .replaceAll("%recipient.unsubscribe_url%", "#preview-unsubscribe");
}

function CompletedSummary({ c }) {
  const rate = (n) => c.total_recipients ? Math.round((n / c.total_recipients) * 100) : 0;
  const items = [
    { label: "Sent", value: c.sent_count, pct: 100 },
    { label: "Delivered", value: c.delivered_count, pct: rate(c.delivered_count) },
    { label: "Opens", value: c.opened_count, pct: rate(c.opened_count) },
    { label: "Clicks", value: c.clicked_count, pct: rate(c.clicked_count) },
    { label: "Bounces", value: c.bounced_count, pct: rate(c.bounced_count) },
    { label: "Unsubs", value: c.unsubscribed_count, pct: rate(c.unsubscribed_count) },
  ];
  return (
    <div className="px-8 py-6 border-b grid grid-cols-6 gap-6" style={{ borderColor:"var(--rahi-border)", background:"#fff" }} data-testid="completed-summary">
      {items.map(({ label, value, pct }) => (
        <div key={label}>
          <div className="text-[10px] uppercase tracking-[0.14em] mb-1.5" style={{ color:"var(--rahi-soft)" }}>{label}</div>
          <div className="font-display text-2xl font-black tracking-tight">{value.toLocaleString("en-IN")}</div>
          <div className="text-[11px] mt-0.5 font-mono" style={{ color:"var(--rahi-muted)" }}>{pct}%</div>
        </div>
      ))}
    </div>
  );
}

function SendConfirmModal({ campaign, activeCount, onCancel, onConfirm }) {
  const [typed, setTyped] = useState("");
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState([]);
  const [preflightCount, setPreflightCount] = useState(activeCount);

  useEffect(() => {
    api.get("/recipients/groups").then(r => {
      const items = r.data.items || [];
      setGroups(items);
      // Default: select just `master` (virtual group = everyone except staff-only).
      // Falls back to all non-staff groups if master isn't present for any reason.
      const hasMaster = items.some(g => g.group === "master" || g.virtual === true);
      if (hasMaster) {
        setSelected(["master"]);
      } else {
        const def = items.map(g => g.group).filter(g => g !== "staff");
        setSelected(def.length ? def : ["donors"]);
      }
    });
  }, []);

  useEffect(() => {
    if (selected.length === 0) { setPreflightCount(0); return; }
    api.post(`/campaigns/${campaign.id}/preflight`, { groups: selected })
      .then(r => setPreflightCount(r.data.active_count))
      .catch(() => setPreflightCount(activeCount));
  }, [selected, campaign.id, activeCount]);

  const toggle = (g) => {
    setSelected(sel => sel.includes(g) ? sel.filter(x => x !== g) : [...sel, g]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background:"rgba(28,25,23,0.6)" }} data-testid="send-confirm-modal">
      <div className="card max-w-lg w-full p-8">
        <div className="w-10 h-[3px] mb-6" style={{ background:"var(--rahi-red)" }}/>
        <h2 className="font-display text-2xl font-black mb-3 tracking-tight">Confirm send</h2>
        <p className="text-sm mb-5" style={{ color:"var(--rahi-muted)" }}>
          You&rsquo;re about to send <strong style={{ color:"var(--rahi-ink)" }}>{campaign.name}</strong>.
        </p>

        {/* Group selector */}
        <div className="mb-5">
          <div className="text-[11px] uppercase tracking-[0.14em] mb-2" style={{ color:"var(--rahi-muted)" }}>Send to groups</div>
          <div className="flex flex-wrap gap-1.5">
            {groups.map(g => {
              const isMaster = g.virtual === true || g.group === "master";
              return (
                <button
                  key={g.group}
                  onClick={() => toggle(g.group)}
                  className={`px-3 py-1.5 rounded-full text-xs border flex items-center gap-1.5 ${selected.includes(g.group) ? (isMaster ? "bg-[#1C1917] text-white border-[#1C1917]" : "bg-[#FF2101] text-white border-[#FF2101]") : (isMaster ? "border-[#1C1917] text-[#1C1917] bg-white hover:bg-[#FBFAF9] font-medium" : "border-[var(--rahi-border)] text-[#57534E] bg-white hover:bg-[#FFF7F5]")}`}
                  data-testid={`send-group-${g.group}`}
                  title={isMaster ? "Virtual: everyone except staff-only records" : g.group}
                >
                  {isMaster && <Star size={11}/>} {g.group} <span className="opacity-75">({g.active.toLocaleString("en-IN")})</span>
                </button>
              );
            })}
          </div>
          <p className="hint mt-2">Pick the recipient groups to include. Each is sent independently.</p>
        </div>

        <p className="text-sm mb-5 px-4 py-3 rounded" style={{ background:"#FFF7F5", color:"#7C2D12" }}>
          Total: <strong style={{ color:"var(--rahi-red)" }}>{preflightCount.toLocaleString("en-IN")}</strong> active recipients across {selected.length} group{selected.length !== 1 ? "s" : ""}. This cannot be undone.
        </p>
        <p className="text-xs mb-3" style={{ color:"var(--rahi-muted)" }}>
          Type <span className="font-mono font-bold">SEND</span> to confirm:
        </p>
        <input value={typed} onChange={(e)=>setTyped(e.target.value)} className="input mb-6" autoFocus data-testid="send-confirm-input"/>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="btn btn-ghost" data-testid="send-cancel-btn">Cancel</button>
          <button onClick={() => onConfirm(selected)} disabled={typed !== "SEND" || selected.length === 0 || preflightCount === 0} className="btn btn-primary" data-testid="send-confirm-btn">
            <Send size={14}/> Send to {preflightCount.toLocaleString("en-IN")}
          </button>
        </div>
      </div>
    </div>
  );
}
